import EverTrue from 'app';
import PropTypes from 'prop-types';
import EmailRecipients from './email-recipients';
import { ActionsMenu, Icon } from '@evertrue/et-components';
import { useEmailDrafts } from '../../drafts/email-drafts-context';
import { useScheduledEmails } from '../../scheduled/scheduled-email-context';
import { useEmailThreads } from '../../threads/email-threads-context';
import { useGlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { useGlobalOutreachProvider } from '../../../contexts/global-outreach-context';
import { isEmpty } from '@evertrue/sdk';
import { useFormatEmailBody } from 'components/emailer/hooks/use-format-email-body';

const EmailCardHeader = ({ email = {} }) => {
  const { email_recipients = [], nylas_schedule_id, status } = email;
  const label = !nylas_schedule_id ? 'Discard Draft' : 'Discard Scheduled';
  const filteredRecipients = email_recipients.filter((recipient) => recipient.recipient_type !== 'FROM');
  const primaryContacts = filteredRecipients.reduce((acc, { recipient_type, contact = {} }) => {
    if (recipient_type === 'TO' && !isEmpty(contact)) {
      return [...acc, contact];
    }
    return acc;
  }, []);

  const { parseBodyForInteraction } = useFormatEmailBody();

  const { deleteDraftById, getAllDraftsByUser } = useEmailDrafts();
  const { deleteScheduledById, cancelScheduled } = useScheduledEmails();
  const { updateSent } = useEmailThreads();

  const { drawers, removeDrawer, addDrawer } = useGlobalDrawerProvider();
  const { openQuickInteraction } = useGlobalOutreachProvider();

  const handleDeleteEmail = async () => {
    const confirmed = await new Promise((resolve) => {
      EverTrue.Alert.confirm(
        {
          headline: label,
          content: `Are you sure you want to ${label.toLowerCase()}?`,
          buttonLabel: 'Discard',
        },
        (confirmation) => resolve(confirmation)
      );
    });

    if (confirmed) {
      const drawer = drawers.find((d) => {
        return d.type === 'emailer' && d.childProps?.email?.id === email?.id;
      });
      if (drawer) {
        removeDrawer(drawer.id);
      }
      if (!nylas_schedule_id) {
        await deleteDraftById(email.id);
      } else {
        await deleteScheduledById(email.id);
      }
    }
  };

  const handleCancelSend = async () => {
    const confirmed = await new Promise((resolve) => {
      EverTrue.Alert.confirm(
        {
          headline: 'Cancel Scheduled Send',
          content: `Are you sure you want to cancel the scheduled send?`,
          buttonLabel: 'Submit',
        },
        (confirmation) => resolve(confirmation)
      );
    });
    if (confirmed) {
      //TODO: FIX WHEN CORRECT RESPONSE IS RETURNED
      const res = await cancelScheduled(email.id);
      // Refresh draft count since the canceled scheduled email becomes a draft
      await getAllDraftsByUser();
      addDrawer('emailer', {
        drawerTitle: `Edit - Draft`,
        email: res,
      });
      EverTrue.UrlManager.navigate('/outreach/email/drafts', true, false);
    }
  };

  const options = [
    {
      id: 2,
      icon: 'trash',
      label,
      onClick: handleDeleteEmail,
    },
  ];

  if (status === 'DRAFT' && nylas_schedule_id) {
    options.unshift({
      id: 1,
      icon: 'cancel',
      label: 'Cancel Scheduled',
      onClick: handleCancelSend,
    });
  }

  const sentOptions = [
    {
      id: 1,
      icon: 'add',
      label: 'Add Interaction',
      onClick: () => {
        openQuickInteraction('emailer', {
          emailId: email.id,
          emailCreatorId: email.creator_user_id,
          updateSent,
          contact: primaryContacts?.[0],
          interaction: {
            summary: email.subject,
            text: `Sent the following email:\n${parseBodyForInteraction(email.body)}`,
          },
        });
      },
    },
  ];

  const shouldHideActionsMenu = (status === 'SENT' || status === 'REPLIED') && primaryContacts.length === 0;

  return (
    <div className="email-card-header">
      <div className="email-card-header-left">
        <EmailRecipients recipients={filteredRecipients} />
      </div>
      <div className="email-card-header-right">
        {!shouldHideActionsMenu && (
          <ActionsMenu
            trigger={({ isOpen }) => <Icon icon="more-horiz" size={1} />}
            options={status === 'SENT' || status === 'REPLIED' ? sentOptions : options}
          />
        )}
      </div>
    </div>
  );
};

EmailCardHeader.propTypes = {
  email: PropTypes.object.isRequired,
};

export default EmailCardHeader;
